import { useEffect, useMemo, useState } from 'react';
import Sidebar from './components/Sidebar';
import EndpointDoc from './components/EndpointDoc';
import RequestPlayground from './components/RequestPlayground';
import SeoHead from './components/SeoHead';
import hawktrionLogo from './assests/logo2.png';
import postmanCollection from './data/whatsapp-cloud-api.postman_collection.json';
import { addMissingApiRoutes, filterApiRoutes, groupOperationsByTag, normalizeOperations, parseOpenApiSpec } from './utils/openapi';
import { hawktrionApiRoutes } from './data/hawktrionApiRoutes';
import {
  createGroupAnchor,
  createOperationSeo,
  createOperationUrl,
  createOverviewSeo
} from './utils/seo';

const THEME_STORAGE_KEY = 'hawktrion-docs-theme';

function getInitialThemeMode() {
  if (typeof window === 'undefined') {
    return false;
  }

  const sessionValue = window.sessionStorage.getItem(THEME_STORAGE_KEY);
  if (sessionValue === 'dark' || sessionValue === 'light') {
    return sessionValue === 'dark';
  }

  const localValue = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (localValue === 'dark' || localValue === 'light') {
    return localValue === 'dark';
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
}

function getPageFromPath(pathname, operations) {
  const operation = operations.find((entry) => createOperationUrl(entry) === pathname.replace(/\/+$/, ''));
  if (operation) {
    return { operationId: operation.id, overviewId: null };
  }

  const overviewId = pathname.match(/^\/overview\/(servers|terms)\/?$/)?.[1] || 'authentication';
  return { operationId: null, overviewId };
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(getInitialThemeMode);
  const [searchTerm, setSearchTerm] = useState('');

  const spec = useMemo(
    () => filterApiRoutes(
      addMissingApiRoutes(parseOpenApiSpec(postmanCollection), hawktrionApiRoutes),
      hawktrionApiRoutes
    ),
    []
  );
  const operations = useMemo(() => normalizeOperations(spec), [spec]);
  const groupedOperations = useMemo(() => groupOperationsByTag(operations), [operations]);
  const [page, setPage] = useState(() => getPageFromPath(window.location.pathname, operations));
  const selectedOverviewId = page.overviewId;
  const selectedOperationId = page.operationId;

  const selectedOperation = useMemo(
    () => operations.find((operation) => operation.id === selectedOperationId) || null,
    [operations, selectedOperationId]
  );

  const activeOperation = selectedOverviewId ? null : selectedOperation;

  const navigateTo = (nextPage, path) => {
    window.history.pushState({}, '', path);
    setPage(nextPage);
    window.scrollTo(0, 0);
  };

  const handleSelectOperation = (operationId) => {
    const operation = operations.find((entry) => entry.id === operationId);
    if (operation) {
      navigateTo({ operationId, overviewId: null }, createOperationUrl(operation));
    }
  };

  const handleSelectOverview = (overviewId) => {
    const path = overviewId === 'authentication' ? '/' : `/overview/${overviewId}`;
    navigateTo({ operationId: null, overviewId }, path);
  };

  useEffect(() => {
    const handlePopState = () => setPage(getPageFromPath(window.location.pathname, operations));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [operations]);

  const seoPage = useMemo(
    () => selectedOperation
      ? createOperationSeo(selectedOperation)
      : createOverviewSeo(selectedOverviewId),
    [selectedOperation, selectedOverviewId]
  );

  useEffect(() => {
    const mode = isDarkMode ? 'dark' : 'light';
    window.sessionStorage.setItem(THEME_STORAGE_KEY, mode);
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [isDarkMode]);

  return (
    <>
      <SeoHead page={seoPage} />
      <div className={isDarkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-[radial-gradient(circle_at_18%_12%,rgba(14,165,233,0.14),transparent_36%),radial-gradient(circle_at_92%_8%,rgba(6,182,212,0.16),transparent_34%),linear-gradient(180deg,#f8fbff_0%,#eef4fb_100%)] font-sans text-slate-900 dark:bg-[radial-gradient(circle_at_18%_12%,rgba(14,165,233,0.2),transparent_36%),radial-gradient(circle_at_92%_8%,rgba(6,182,212,0.16),transparent_34%),linear-gradient(180deg,#090d14_0%,#0f1623_100%)] dark:text-slate-100">
        <header className="sticky top-0 z-20 border-b border-white/10 bg-[rgba(10,15,31,0.9)] text-white backdrop-blur">
          <div className="mx-auto flex w-full flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <img
                src={hawktrionLogo}
                alt="Hawktrion logo"
                className="h-12 w-auto shrink-0 rounded-md border border-cyan-300/40 object-contain sm:h-14"
              />
              <div className="min-w-0">
                <p className="bg-gradient-to-r from-[#00A7FF] to-[#00F7D2] bg-clip-text text-xs font-semibold uppercase tracking-[0.2em] text-transparent">Hawktrion Documentation</p>
                <h1 className="text-sm font-semibold text-white sm:text-base">Hawktrion WhatsApp Business Solution Provider API</h1>
                <p className="mt-1 text-[11px] text-slate-300">
                  Based on {spec.info?.title || 'WhatsApp Cloud API'} specifications.
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                  <a
                    href="https://waba.hawktrion.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[#00D7EA] hover:text-[#00F7D2] hover:underline"
                  >
                    WABA Portal
                  </a>
                  <a
                    href="https://hawktrion.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[#00D7EA] hover:text-[#00F7D2] hover:underline"
                  >
                    Main Website
                  </a>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="self-start rounded-lg border border-cyan-300/40 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 sm:self-center"
              onClick={() => setIsDarkMode((previous) => !previous)}
            >
              {isDarkMode ? 'Light mode' : 'Dark mode'}
            </button>
          </div>
        </header>

        <main className="mx-auto grid min-h-[calc(100vh-96px)] w-full grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:max-w-[1510px] xl:grid-cols-[280px_minmax(0,800px)_430px]">
          <Sidebar
            groupedOperations={groupedOperations}
            selectedOperationId={selectedOperationId}
            selectedOverviewId={selectedOverviewId}
            onSelectOperation={handleSelectOperation}
            onSelectOverview={handleSelectOverview}
            getOperationUrl={createOperationUrl}
            getGroupAnchor={createGroupAnchor}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />

          <section className="min-w-0 overflow-y-auto p-4 lg:p-6">
            <EndpointDoc spec={spec} operation={activeOperation} selectedOverviewId={selectedOverviewId} />
          </section>

          <section className="min-w-0 border-t border-slate-200 p-4 lg:col-start-2 lg:p-6 xl:col-start-auto xl:border-l xl:border-t-0 dark:border-slate-800 xl:sticky xl:top-24 xl:h-[calc(100vh-96px)] xl:overflow-y-auto">
            <RequestPlayground spec={spec} operation={selectedOperation} />
          </section>
        </main>
      </div>
      </div>
    </>
  );
}

export default App;
